import * as React from 'react';
import {useState} from 'react';
import {useForm} from "react-hook-form";
import {Alert, Chip, CircularProgress, Snackbar, TextField} from "@mui/material";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import ContentItem from "../content-item/ContentItem";
import {LoadingItem} from "../loading-item/LoadingItem";
import ProviderCreateRequestDto from "../../dto/ProviderCreateRequestDto";
import {useCreateProviderMutation} from "../../store/apis/providerApi";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddIcon from '@mui/icons-material/Add';

interface ProviderFormProps {
    onSubmit: () => void;
}

function ProviderForm(props: ProviderFormProps) {
    const {onSubmit: onSubmitProviderForm} = props;
    const {t} = useTranslation();
    const [createProvider, {error, isLoading}] = useCreateProviderMutation();
    const {register, handleSubmit, setValue, formState: {errors}} = useForm();
    const [emails, setEmails] = useState<string[]>([]);
    const [inputValue, setInputValue] = useState<string>("");
    const [validationAlertOpen, setValidationAlertOpen] = useState<boolean>(false);
    const [validationMessage, setValidationMessage] = useState<string>('');
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddEmail = () => {
        if (!inputValue.trim()) return;

        if (validateEmail(inputValue)) {
            if (!emails.includes(inputValue)) {
                setEmails((prev) => [...prev, inputValue]);
                setValue("expenseEmails", [...emails, inputValue]);
                setInputValue("");
            } else {
                openValidationAlert(t("Email już istnieje na liście."));
            }
        } else {
            openValidationAlert(t("Nieprawidłowy format emaila."));
        }
    };

    const handleDeleteEmail = (emailToRemove: string) => {
        setEmails((prev) => prev.filter((email) => email !== emailToRemove));
    };

    const openValidationAlert = (message: string) => {
        setValidationMessage(message);
        setValidationAlertOpen(true);
    }

    const onSubmit = async (data: any) => {
        const providerData = data as ProviderCreateRequestDto;
        await createProvider(providerData);
        onSubmitProviderForm();
    };

    const handleValidationAlertClose = () => {
        setValidationMessage('');
        setValidationAlertOpen(false);
    }

    return (
        <Box sx={{width: '100%'}}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <ContentItem>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            fullWidth
                            defaultValue="" {...register("name", {required: true})}
                            label={t('Company name')}
                            helperText={errors.name && t('Name is required')}
                            multiline
                        />
                        <Typography variant="h6">{t('Lista emaili z których będziesz wysyłać dokumenty')}</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {emails.map((email, index) => (
                                <Chip
                                    key={index}
                                    label={email}
                                    onDelete={() => handleDeleteEmail(email)}
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                                label={t("Dodaj email")}
                                variant="outlined"
                                size="small"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddEmail();
                                    }
                                }}
                            />
                            <IconButton color="primary" onClick={handleAddEmail}>
                                <AddIcon/>
                            </IconButton>
                            <Snackbar
                                open={validationAlertOpen}
                                autoHideDuration={6000}
                                onClose={handleValidationAlertClose}
                            >
                                <Alert
                                    onClose={handleValidationAlertClose}
                                    severity="error"
                                    variant="filled"
                                    sx={{width: '100%'}}
                                >
                                    {validationMessage}
                                </Alert>
                            </Snackbar>
                        </Box>
                        {isLoading && (<LoadingItem><CircularProgress/></LoadingItem>)}
                        {error && (<Alert severity="error">{t("Cannot create company profile!")}</Alert>)}
                        {!isLoading && (<Button variant="contained" type="submit">{t('Create')}</Button>)}
                    </Box>
                </ContentItem>
            </form>
        </Box>
    );
}

export default ProviderForm;